/* Created by Interactive 63 - https://interactive63.com */
/**
 * Named editor actions. Each is a thin wrapper that applies the keyframe helpers or settings
 * changes to the store with a clear undo label. UI code calls these rather than touching the
 * project directly.
 */
import {
  clampKeyframesToDuration,
  canAddProperty,
  moveKeyframes as moveKeyframeHelper,
  removeKeyframes,
  scaleKeyframeTimes,
  setKeyframe,
  setKeyframeEasing as setKeyframeEasingHelper,
  setKeyframeValue as setKeyframeValueHelper,
} from '../../core/timeline/keyframes';
import type {
  Easing,
  ElementId,
  ExportSettings,
  MediaSettings,
  PropertyKey,
  PropertyValue,
  SceneSettings,
  Vec2,
} from '../../core/model/types';
import type { Store } from './store';

export type SelectionMode = 'replace' | 'add' | 'toggle';

const MIN_DURATION = 100;
const MAX_DURATION = 120000;

/** Combines a current selection with new IDs according to the selection mode. */
function combine(
  current: readonly string[],
  ids: readonly string[],
  mode: SelectionMode,
): string[] {
  switch (mode) {
    case 'replace':
      return [...new Set(ids)];
    case 'add':
      return [...new Set([...current, ...ids])];
    case 'toggle': {
      const toggled = new Set(ids);
      return [
        ...current.filter((id) => !toggled.has(id)),
        ...[...toggled].filter((id) => !current.includes(id)),
      ];
    }
  }
}

/** Adds or updates a keyframe. Returns false when the property is not allowed on the element. */
export function addKeyframe(
  store: Store,
  elementId: ElementId,
  property: PropertyKey,
  time: number,
  value: PropertyValue,
  easing?: Easing,
): boolean {
  if (!canAddProperty(store.getState().project, elementId, property)) return false;
  store.updateProject('Add keyframe', (draft) => {
    setKeyframe(draft, elementId, property, time, value, easing);
  });
  return true;
}

/** Deletes the keyframes currently selected in the editor. */
export function deleteSelectedKeyframes(store: Store): void {
  const ids = store.getState().editor.selection.keyframeIds;
  if (ids.length === 0) return;
  store.transaction('Delete keyframes', () => {
    store.updateProject('Delete keyframes', (draft) => {
      removeKeyframes(draft, ids);
    });
    store.updateEditor((draft) => {
      draft.selection.keyframeIds = [];
    });
  });
}

/** Moves keyframes by a delta. Pass a coalesce key while dragging so it is one undo step. */
export function moveKeyframes(
  store: Store,
  ids: readonly string[],
  deltaMs: number,
  coalesceKey?: string,
): void {
  store.updateProject(
    'Move keyframes',
    (draft) => {
      moveKeyframeHelper(draft, ids, deltaMs);
    },
    { coalesceKey },
  );
}

/** Sets the easing of the given keyframes. */
export function setKeyframeEasing(
  store: Store,
  ids: readonly string[],
  easing: Easing,
  coalesceKey?: string,
): void {
  store.updateProject(
    'Change easing',
    (draft) => {
      setKeyframeEasingHelper(draft, ids, easing);
    },
    { coalesceKey },
  );
}

/** Sets the value of one keyframe. Pass a coalesce key while scrubbing a control. */
export function setKeyframeValue(
  store: Store,
  id: string,
  value: PropertyValue,
  coalesceKey?: string,
): void {
  store.updateProject(
    'Edit keyframe value',
    (draft) => {
      setKeyframeValueHelper(draft, id, value);
    },
    { coalesceKey },
  );
}

/**
 * Updates scene settings. A changed duration is clamped to the allowed range; keyframes are then
 * either scaled with it or, if not scaling, pulled back inside the new duration.
 */
export function setSceneSettings(
  store: Store,
  partial: Partial<SceneSettings>,
  options?: { scaleKeyframes?: boolean; coalesceKey?: string },
): void {
  store.updateProject(
    'Change scene settings',
    (draft) => {
      const previous = draft.scene.duration;
      Object.assign(draft.scene, partial);
      if (partial.duration === undefined) return;
      draft.scene.duration = Math.min(
        MAX_DURATION,
        Math.max(MIN_DURATION, Math.round(partial.duration)),
      );
      if (options?.scaleKeyframes) scaleKeyframeTimes(draft, draft.scene.duration / previous);
      clampKeyframesToDuration(draft);
    },
    { coalesceKey: options?.coalesceKey },
  );
}

/** Updates export settings. */
export function setExportSettings(
  store: Store,
  partial: Partial<ExportSettings>,
  coalesceKey?: string,
): void {
  store.updateProject(
    'Change export settings',
    (draft) => {
      Object.assign(draft.export, partial);
    },
    { coalesceKey },
  );
}

/** Updates the GIF and MP4 settings. */
export function setMediaSettings(
  store: Store,
  partial: Partial<MediaSettings>,
  coalesceKey?: string,
): void {
  store.updateProject(
    'Change export settings',
    (draft) => {
      Object.assign(draft.export.media, partial);
    },
    { coalesceKey },
  );
}

/** Sets an element's transform origin, as percentages of its bounding box. */
export function setElementOrigin(
  store: Store,
  elementId: ElementId,
  origin: Vec2,
  coalesceKey?: string,
): void {
  store.updateProject(
    'Move origin',
    (draft) => {
      const element = draft.elements[elementId];
      if (element) element.origin = origin;
    },
    { coalesceKey },
  );
}

/** Renames an element's layer label. Blank names are ignored. */
export function renameElement(store: Store, elementId: ElementId, label: string): void {
  const trimmed = label.trim();
  if (trimmed === '') return;
  store.updateProject('Rename layer', (draft) => {
    const element = draft.elements[elementId];
    if (element) element.label = trimmed;
  });
}

/** Enables or disables a track. */
export function toggleTrackEnabled(store: Store, trackId: string): void {
  store.updateProject('Toggle track', (draft) => {
    const track = draft.tracks.find((t) => t.id === trackId);
    if (track) track.enabled = !track.enabled;
  });
}

/** Changes the element selection. */
export function selectElements(
  store: Store,
  ids: readonly ElementId[],
  mode: SelectionMode = 'replace',
): void {
  store.updateEditor((draft) => {
    draft.selection.elementIds = combine(draft.selection.elementIds, ids, mode);
  });
}

/** Changes the keyframe selection. */
export function selectKeyframes(
  store: Store,
  ids: readonly string[],
  mode: SelectionMode = 'replace',
): void {
  store.updateEditor((draft) => {
    draft.selection.keyframeIds = combine(draft.selection.keyframeIds, ids, mode);
  });
}

/** Clears both element and keyframe selection. */
export function clearSelection(store: Store): void {
  store.updateEditor((draft) => {
    draft.selection.elementIds = [];
    draft.selection.keyframeIds = [];
  });
}

/** Moves the playhead, clamped to the scene. */
export function setPlayhead(store: Store, ms: number): void {
  const duration = store.getState().project.scene.duration;
  store.updateEditor((draft) => {
    draft.playhead = Math.min(duration, Math.max(0, ms));
  });
}

/** Renames the project. Blank names are ignored. */
export function renameProject(store: Store, name: string): void {
  const trimmed = name.trim();
  if (trimmed === '') return;
  store.updateProject('Rename project', (draft) => {
    draft.name = trimmed;
  });
}

/** Shows or hides a layer in the editor only. */
export function toggleElementHidden(store: Store, elementId: ElementId): void {
  store.updateProject('Toggle layer visibility', (draft) => {
    const element = draft.elements[elementId];
    if (element) element.hiddenInEditor = !element.hiddenInEditor;
  });
}

/** Includes or excludes an element from the exported animation. */
export function toggleElementExcluded(store: Store, elementId: ElementId): void {
  store.updateProject('Toggle export', (draft) => {
    const element = draft.elements[elementId];
    if (element) element.excludeFromExport = !element.excludeFromExport;
  });
}

/** Expands or collapses layers in the tree. */
export function setExpanded(store: Store, ids: readonly string[], expanded: boolean): void {
  store.updateEditor((draft) => {
    const current = new Set(draft.expandedElementIds);
    for (const id of ids) {
      if (expanded) current.add(id);
      else current.delete(id);
    }
    draft.expandedElementIds = [...current];
  });
}

/** Records which element the pointer is over, for hover outlines in the stage and layers. */
export function setHovered(store: Store, elementId: ElementId | null): void {
  store.updateEditor((draft) => {
    draft.hoveredElementId = elementId;
  });
}

/** Sets the stage zoom and pan. */
export function setStageView(store: Store, zoom: number, pan: Vec2): void {
  store.updateEditor((draft) => {
    draft.stageZoom = zoom;
    draft.stagePan = pan;
  });
}

/** Turns auto-key on or off. */
export function setAutoKey(store: Store, enabled: boolean): void {
  store.updateEditor((draft) => {
    draft.autoKey = enabled;
  });
}
